import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listSubtasks, createTask, completeTask, type Task } from '../api';
import { SubtaskPill } from './SubtaskPill';
import { StatusBadge } from './StatusBadge';
import { STATUS_VERB, displayStatus, dueLook, dueClass, dueTitle } from '../taskVocab';
import { useAccountDay } from '../accountDay';
import { useProjectRole } from '../roles';

/**
 * A task's subtasks, as work rather than as a caption.
 *
 * The section listed them as inert text with a tick — not clickable, no status beyond done, no
 * estimate, and no way to add one. A subtask could only be made by creating a task somewhere else
 * and then setting its parent, which is three screens for what is one thought.
 *
 * The one-line input is the pattern steps already prove: type, Enter, next one. It keeps focus
 * deliberately — breaking work up happens in a burst, and a field that empties and stays put is
 * the whole of "easy to create".
 *
 * Shared by the detail panel and the full view so the two cannot drift, which is the failure this
 * codebase keeps finding whenever a surface draws its own version of a row.
 */
export function Subtasks({
  taskId,
  projectId,
  parent,
  onOpenTask,
  startOpen = false,
}: {
  taskId: string;
  /**
   * The project the parent sits in, or null for personal work — the only thing this section was
   * missing to know whether it may offer what it offers.
   *
   * Required rather than optional so a third surface cannot take a default of "allowed" the way
   * `AssigneeLine.actionable` did, which is how "Take it on" survived a fix that removed it from
   * the row one click above.
   */
  projectId: string | null;
  /** The parent itself, for the `K/N` pill — the same one its row shows, and its own parent. */
  parent: Pick<Task, 'subtasks' | 'parentTaskId'>;
  onOpenTask?: (id: string) => void;
  /** Asked for from the offer in *More* — see `Checklist` in `TaskDetail.tsx`. */
  startOpen?: boolean;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  /* The account's calendar, so a subtask's pill and its parent's row cannot disagree about which
     day it is — this read the browser's. Above the early return: hooks run unconditionally. */
  const { timezone, today } = useAccountDay();
  /** Opened from the collapsed line — see the empty branch below. */
  const [adding, setAdding] = useState(startOpen);
  const input = useRef<HTMLInputElement>(null);
  const subtasks = useQuery({
    queryKey: ['tasks', taskId, 'subtasks'],
    queryFn: () => listSubtasks(taskId),
  });

  /*
    `write`, because a subtask lands in the parent's project and filing work into a project is
    `write` — the same level `FIELD_LEVEL.parentTaskId` charges for moving an existing task under
    one. It was not what the server charged: `createTask` authorised the project the caller
    *named*, and this form names none, so a reporter's subtask was accepted. Both halves moved in
    the same commit; the screen asking is only useful while the server refuses.
  */
  const mayAdd = useProjectRole(projectId).can('write');

  const add = useMutation({
    mutationFn: (t: string) => createTask({ title: t, parentTaskId: taskId }),
    onSuccess: () => {
      setTitle('');
      /*
        Invalidate everything, not just this list.

        Creating a subtask moves the parent's `K/N` on six other surfaces, and the subtask
        inherits goals and labels that the goal screens count. A narrower key would leave the
        pill on the row behind this panel reading the old number.
      */
      void queryClient.invalidateQueries();
      input.current?.focus();
    },
  });

  /*
    Done last, and struck through.

    Sorted here rather than asked of the server: the list is a handful of rows, and the server's
    order is `sortOrder` then creation, which is the order they were written in and worth keeping
    among the ones still to do.
  */
  const rows = [...(subtasks.data ?? [])].sort((a, b) => {
    const done = (t: Task) => (t.status === 'done' ? 1 : 0);
    return done(a) - done(b);
  });

  /*
    Empty costs one line, not a panel — and says what a subtask is.

    Subtasks and the checklist rendered identically and behaved oppositely: same heading, same empty
    state, same Add button, stacked one above the other. One creates a schedulable task, the other
    a line of text, and nothing on the screen said so. Four empty panels came to 475px of a 589px
    view.

    A task that already has a parent gets the rule instead, since it cannot have one either way.
  */
  if (rows.length === 0 && !adding) {
    return parent.parentTaskId === null ? (
      <p className="panel-empty">
        {/* Greyed, not removed: the surface says once what this role may do, and a control that
            has quietly gone is a thing the reader cannot ask about. Same call as Complete above. */}
        <button className="link" onClick={() => setAdding(true)} disabled={!mayAdd}>
          + a subtask
        </button>{' '}
        {/* The other half of the step/subtask distinction — see the note beside `+ a step`. */}
        <span className="subtext">a piece of work in its own right — it can have a day</span>
      </p>
    ) : (
      <p className="panel-empty" data-testid="one-level-only">
        <span className="subtext">
          Subtasks are one level deep, so this one cannot have its own.
        </span>
      </p>
    );
  }

  return (
    <div className="panel">
      <h3>
        Subtasks <SubtaskPill task={parent} />
      </h3>

      {rows.length > 0 && (
        <ul className="plain subtask-list">
          {rows.map((s) => {
            const look = dueLook(s, today, timezone);
            return (
              <li key={s.id} data-testid="subtask-row">
                <button
                  className={`link subtask-title ${s.status === 'done' ? 'struck' : ''}`}
                  onClick={() => onOpenTask?.(s.id)}
                  disabled={!onOpenTask}
                >
                  {s.title}
                </button>
                <span className="task-meta">
                  {/*
                    Its own state, not just whether it is finished. A subtask is a piece of work —
                    it can be in progress, it can have a size, it can be planned for Tuesday — and
                    a tick said none of that.
                  */}
                  <StatusBadge status={displayStatus(s)} compact />
                  {s.estimateMinutes !== null && <span className="pill">{s.estimateMinutes}m</span>}
                  {s.scheduledDate && <span className="pill">{s.scheduledDate.slice(5)}</span>}
                  {look && (
                    <span className={dueClass(look)} title={dueTitle(look)}>
                      {look.text}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        A task that is already a subtask is offered no way to gain one.

        Nesting stops at one level and the API enforces it, so this form existed only to be
        refused — which teaches that the app is unreliable rather than that the rule exists. The
        rule is stated where the control was, in a word, because "why can't I" is the question a
        missing control raises.

        Presentation only: the server rule is untouched, and it stays the thing that decides.
      */}
      {parent.parentTaskId === null ? (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) add.mutate(title.trim());
          }}
        >
          <input
            ref={input}
            value={title}
            placeholder="Add a subtask"
            aria-label="Add a subtask"
            disabled={!mayAdd}
            onChange={(e) => setTitle(e.target.value)}
          />
          <button type="submit" disabled={!title.trim() || add.isPending || !mayAdd}>
            Add
          </button>
        </form>
      ) : (
        <p className="muted" data-testid="one-level-only">
          Subtasks are one level deep, so this one cannot have its own.
        </p>
      )}
    </div>
  );
}

/**
 * The offer to finish the parent once every part of it is done.
 *
 * **Offered, never taken.** A parent usually has work of its own beyond its parts — the review,
 * the sending, the washing up — and a task that closes itself is a task the user did not close.
 * So this is a question with a button, and ignoring it costs nothing.
 *
 * It reads the parent's own `K/N` rather than firing from wherever a subtask was completed: a
 * subtask can be finished from the row, the board, the command bar or its own panel, and an offer
 * wired into four places is an offer that will be missing from one.
 *
 * Rendered beside the blocked banner rather than down in the subtask section, which is where it
 * started and where it was below the fold — the person who just ticked the last subtask would
 * have had to go looking for it. This is the panel's established place for a fact about the task
 * that wants an answer.
 */
export function SubtaskOffer({ task }: { task: Pick<Task, 'id' | 'subtasks' | 'status'> }) {
  const queryClient = useQueryClient();
  const finish = useMutation({
    mutationFn: () => completeTask(task.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const { done, total } = task.subtasks;
  if (total === 0 || done !== total || task.status === 'done') return null;
  return (
    <p className="banner subtask-offer" data-testid="subtask-offer">
      {total === 1 ? 'Its subtask is done.' : `All ${total} subtasks are done.`}{' '}
      <button className="link" onClick={() => finish.mutate()} disabled={finish.isPending}>
        {STATUS_VERB.done.verb} it too?
      </button>
    </p>
  );
}
